import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  useIsMobile,
} from '@dotfile/frontend/shared/design-system';
import {
  ClientPortalChecks_ElectronicSignatureCheck,
  ClientPortalChecks_Individual,
} from '@dotfile/shared/data-access-client-portal';

import { CheckModalHeader } from '../shared';
import { YousignSdkProvider } from '../shared/contexts';
import {
  ELECTRONIC_SIGNATURE_IFRAME_HEIGHT,
  ELECTRONIC_SIGNATURE_IFRAME_HEIGHT_MOBILE,
  ElectronicSignatureVendorIframe,
  SignatureSuccessMessage,
} from './components';
import { usePollElectronicSignatureCheck } from './hooks';

type ElectronicSignatureModalProps = {
  check: ClientPortalChecks_ElectronicSignatureCheck;
  individual: ClientPortalChecks_Individual;
  isOpen: boolean;
  onClose: () => void;
};

export const ElectronicSignatureModal = ({
  check,
  individual,
  isOpen,
  onClose,
}: ElectronicSignatureModalProps) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [isSignatureComplete, setIsSignatureComplete] = useState(false);

  const signatureLink = check.data?.vendor?.signatureLink;
  const vendor = check.data?.vendor?.name;

  const { start: startPollElectronicSignatureCheck, loading: pollingLoading } =
    usePollElectronicSignatureCheck(check.id, onClose);

  const handleSignatureComplete = useCallback(async () => {
    await startPollElectronicSignatureCheck();
    setIsSignatureComplete(true);
  }, [startPollElectronicSignatureCheck]);

  const handleClose = useCallback(() => {
    setIsSignatureComplete(false);
    onClose();
  }, [onClose]);

  return (
    <Modal
      variant="dialog-white"
      isOpen={isOpen}
      size={isMobile ? 'full' : '4xl'}
      onClose={handleClose}
      isCentered
      scrollBehavior="inside"
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>
          <CheckModalHeader check={check} />
        </ModalHeader>
        <ModalBody position="relative" p="0" display="flex" flex="1">
          {isSignatureComplete || pollingLoading ? (
            <SignatureSuccessMessage />
          ) : signatureLink && vendor ? (
            <YousignSdkProvider>
              <ElectronicSignatureVendorIframe
                vendor={vendor}
                signatureLink={signatureLink}
                onComplete={handleSignatureComplete}
              />
            </YousignSdkProvider>
          ) : (
            <Box
              minHeight={
                isMobile
                  ? ELECTRONIC_SIGNATURE_IFRAME_HEIGHT_MOBILE
                  : ELECTRONIC_SIGNATURE_IFRAME_HEIGHT
              }
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="100%"
              flex="1"
            >
              <Spinner size="xl" color="custom.500" />
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={handleClose}>
            {isSignatureComplete
              ? t('common.close', {
                  defaultValue: 'Close',
                  ns: 'client-portal',
                })
              : t('common.cancel', {
                  defaultValue: 'Cancel',
                  ns: 'client-portal',
                })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
